import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentlyViewedStep,
  getMaxUnlockedTutorialStep,
  getTutorialStatus,
  getTutorialTotalSteps,
} from '../../reducers/TutorialReducer/TutorialSelectors';
import { formatMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import * as React from 'react';
import { useState } from 'react';
import { useTutorialSteps } from '../../shared/api/apiHooks';

import './TutorialButtons.scss';
import { TutorialActions } from '../../reducers/TutorialReducer/TutorialReducer';
import { Popup } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import analytics from '../../shared/services/analytics/analytics';
import Parser from 'html-react-parser';

interface TutorialButtonsProps {
  onFinishTutorial?: () => void;
}

export function TutorialButtons({ onFinishTutorial }: TutorialButtonsProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isHintShown, setIsHintShown] = useState(false);
  const [isAnswerShown, setIsAnswerShown] = useState(false);

  const currentPage = useSelector(getCurrentlyViewedStep);
  const tutorialStatus = useSelector(getTutorialStatus);
  const pageCount = useSelector(getTutorialTotalSteps);
  const maxUnlockedPage = useSelector(getMaxUnlockedTutorialStep);
  const isNextPageAvailable = currentPage < maxUnlockedPage || (currentPage === pageCount - 1 && tutorialStatus === 'COMPLETED');
  const isPreviousPageAvailable = currentPage > 0;
  const isLastPage = currentPage === pageCount;

  const { data: tutorialHints } = useTutorialSteps();
  const currentHint = tutorialHints?.[currentPage]?.hint;
  const currentAnswer = tutorialHints?.[currentPage]?.answer;

  function handleNext() {
    dispatch(TutorialActions.setNextStep());
    setIsHintShown(false);
    setIsAnswerShown(false);
  }

  function handlePrevious() {
    dispatch(TutorialActions.setPreviousStep());
    setIsHintShown(false);
    setIsAnswerShown(false);
  }

  const handleShowAnswer = () => {
    analytics.trackCustomEvent('tutorial_click_show_answer', { event_label: currentPage.toString() });
    setIsAnswerShown(true);
  };
  const handleShowHint = () => {
    analytics.trackCustomEvent('tutorial_click_show_hint', { event_label: currentPage.toString() });
    setIsHintShown(true);
  };
  const isHintSectionShown = currentPage !== pageCount - 1 && (currentHint || currentAnswer);
  return (
    <>
      {isHintSectionShown && (
        <div className="tutorial-buttons__hint-section">
          {currentHint && (
            <div className="tutorial-buttons__hint">
              {isHintShown ? (
                <div>
                  <FormattedMessageHelper tagName="strong" id="navigatorTutorial.hint" />
                  {Parser(currentHint)}
                </div>
              ) : (
                <button className="ui basic button" onClick={handleShowHint}>
                  <FormattedMessageHelper tagName="span" id="navigatorTutorial.showHint" />
                </button>
              )}
            </div>
          )}
          {currentAnswer && (
            <div className="tutorial-buttons__answer">
              {isAnswerShown ? (
                <div>
                  <FormattedMessageHelper tagName="strong" id="navigatorTutorial.answer" />
                  {Parser(currentAnswer)}
                </div>
              ) : (
                <button className="ui basic button" onClick={handleShowAnswer}>
                  <FormattedMessageHelper tagName="span" id="navigatorTutorial.showAnswer" />
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {currentPage === pageCount - 1 && <div className="final-step-content"></div>}
      <div className="tutorial-buttons">
        <button
          className="tutorial-buttons__button ui button button--secondary"
          onClick={handlePrevious}
          disabled={!isPreviousPageAvailable}
        >
          <FormattedMessageHelper tagName="span" id="navigatorTutorial.previousPage" />
        </button>
        {isLastPage ? (
          <button className="tutorial-buttons__button ui primary button" onClick={onFinishTutorial}>
            <FormattedMessageHelper tagName="span" id="navigatorTutorial.finish" />
          </button>
        ) : (
          <Popup
            on="hover"
            mouseEnterDelay={80}
            disabled={isNextPageAvailable}
            position="top right"
            content={formatMessageHelper(intl, 'navigatorTutorial.blockedNextHint')}
            trigger={
              <div>
                <button className="tutorial-buttons__button ui primary button" onClick={handleNext} disabled={!isNextPageAvailable}>
                  <FormattedMessageHelper tagName="span" id="navigatorTutorial.nextPage" />
                </button>
              </div>
            }
          />
        )}
      </div>
    </>
  );
}
